import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCartCount } from "@/hooks/useCartCount";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { logout } from '../utils/api';
import { toast } from 'sonner';
import { User as UserIcon, Package, LogOut, HelpCircle, Edit2, Save, X, Check, Clock, ShoppingCart, Trash2, Plus, Minus } from 'lucide-react';
import NavigationBar from '@/components/NavigationBar';
import Footer from '@/components/Footer';
import AssetScheduler from '@/components/AssetScheduler';
import InterestFormModal from '@/components/InterestFormModal';
import PaymentModal from '@/components/PaymentModal';
import CancellationModal from '@/components/CancellationModal';
import ProcessTracker from '@/components/ProcessTracker';
import axios from 'axios';
import { getCart, updateCartItem, deleteCartItem, getAsset, getCurationPackage } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

// Countdown Timer Component
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(targetDate) - new Date();
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        
        return `${days}d ${hours}h ${minutes}m`;
      }
      return 'Ready for deployment';
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <span className="flex items-center gap-1">
      <Clock size={16} />
      {timeLeft}
    </span>
  );
};

const MemberDashboard = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [myAssets, setMyAssets] = useState([]);
  const [showReferralPopup, setShowReferralPopup] = useState(false);
  const [referralMessage, setReferralMessage] = useState('');
  const [selectedAssetForReferral, setSelectedAssetForReferral] = useState(null);

  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [tickets, setTickets] = useState([]);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    message: '',
    first_name: '',
    last_name: '',
    email: '',
    contact_number: ''
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastTicketNumber, setLastTicketNumber] = useState('');
  const [showInterestFormModal, setShowInterestFormModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [selectedAssetForCancellation, setSelectedAssetForCancellation] = useState(null);
  const [expandedSchedulers, setExpandedSchedulers] = useState({});  // Track which schedulers are expanded
  const [cartItems, setCartItems] = useState([]);
  const [cartAssets, setCartAssets] = useState({});
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const navigate = useNavigate();
  const { cartCount, refreshCartCount } = useCartCount(user);

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        country_code: user.country_code || '+91',
        whatsapp_number: user.whatsapp_number || '',
        state: user.state || '',
        city: user.city || ''
      });

      setTicketForm({
        ...ticketForm,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        contact_number: user.phone || ''
      });
    }
  }, [user]);

  // Separate effect for tab-specific data loading
  useEffect(() => {
    if (!user) return;
    
    if (activeTab === 'assets') {
      fetchUserAssets();
    } else if (activeTab === 'support') {
      loadTickets();
    } else if (activeTab === 'cart') {
      loadCart();
    }
  }, [activeTab, user]);

  // Handle tab query parameter on mount only
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, []);

  const loadCart = async () => {
    try {
      const response = await getCart();
      const cartData = response.data;
      
      // Fetch details for each cart item (asset or curation package)
      const items = {};
      const validCartItems = [];
      const itemsToDelete = [];
      
      for (const item of cartData) {
        try {
          if (item.item_type === 'curation_package') {
            // Need to get curation package by ID - first get all packages
            const packagesResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/curation-packages`);
            const pkg = packagesResponse.data.find(p => p.id === item.asset_id);
            if (pkg) {
              items[item.asset_id] = { ...pkg, item_type: 'curation_package' };
              validCartItems.push(item);
            } else {
              // Package not found - mark for deletion
              console.warn(`Curation package ${item.asset_id} not found - removing from cart`);
              itemsToDelete.push(item.id);
            }
          } else {
            const assetResponse = await getAsset(item.asset_id);
            items[item.asset_id] = { ...assetResponse.data, item_type: 'asset' };
            validCartItems.push(item);
          }
        } catch (error) {
          // Asset/package doesn't exist or failed to load - mark for deletion
          console.warn(`Failed to load item ${item.asset_id} - removing from cart:`, error.message);
          itemsToDelete.push(item.id);
        }
      }
      
      // Delete orphaned cart items
      if (itemsToDelete.length > 0) {
        console.log(`Cleaning up ${itemsToDelete.length} orphaned cart items...`);
        for (const cartItemId of itemsToDelete) {
          try {
            await deleteCartItem(cartItemId);
          } catch (error) {
            console.error(`Failed to delete orphaned cart item ${cartItemId}:`, error);
          }
        }
        toast.info('Removed unavailable items from cart');
      }
      
      // Update state with only valid items
      setCartItems(validCartItems);
      setCartAssets(items);
      
      // Dispatch cart update event for other components
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      console.error('Failed to load cart:', error);
      toast.error('Failed to load cart items');
    }
  };

  const handleUpdateCartItem = async (cartItemId, newTickets) => {
    try {
      await updateCartItem(cartItemId, { tickets: newTickets });
      toast.success('Cart updated successfully');
      loadCart();
      setEditingCartItem(null);
      // Dispatch cart update event for other components
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      toast.error('Failed to update cart item');
    }
  };

  const handleDeleteCartItem = async (cartItemId) => {
    try {
      await deleteCartItem(cartItemId);
      toast.success('Item removed from cart');
      loadCart();
      // Dispatch cart update event for other components
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleProceedToExpressInterest = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    try {
      console.log('Starting express interest submission...', { cartItems });
      
      // Move items from cart to assets
      for (const cartItem of cartItems) {
        const asset = cartAssets[cartItem.asset_id];
        const pricePerTicket = asset?.price_per_ticket || 0;
        
        const assetData = {
          user_id: user.id,
          asset_id: cartItem.asset_id,
          asset_type: cartItem.item_type || 'asset',
          tickets: cartItem.tickets,
          price_per_ticket: pricePerTicket,
          status: 'expression_of_interest',
          stage: 'expression_of_interest',
          stage_description: 'Expression of Interest submitted - Awaiting next steps'
        };

        console.log('Creating user asset:', assetData);

        // Add to user assets
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user-assets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(assetData)
        });

        if (!response.ok) {
          const errorData = await response.text();
          console.error('API Error:', response.status, errorData);
          throw new Error(`Failed to create user asset: ${response.status} ${errorData}`);
        }

        const result = await response.json();
        console.log('User asset created successfully:', result);

        // Remove from cart
        console.log('Removing cart item:', cartItem.id);
        await deleteCartItem(cartItem.id);
        console.log('Cart item removed successfully');
      }

      // Refresh data immediately
      console.log('Refreshing cart and assets data...');
      await loadCart();
      await fetchUserAssets();
      console.log('Data refreshed successfully');
      
      setActiveTab('assets'); // Switch to assets tab to show the moved items
      toast.success('Expression of Interest submitted! Assets moved to My Assets section.');
    } catch (error) {
      console.error('Error processing express interest:', error);
      toast.error(`Failed to submit expression of interest: ${error.message || 'Please try again.'}`);
    }
  };

  const handlePaymentSubmit = async () => {
    try {
      console.log('Starting payment submission...', { cartItems });
      
      // Move items from cart to assets
      for (const cartItem of cartItems) {
        const asset = cartAssets[cartItem.asset_id];
        const pricePerTicket = asset?.price_per_ticket || 0;
        
        const assetData = {
          user_id: user.id,
          asset_id: cartItem.asset_id,
          asset_type: cartItem.item_type || 'asset', // Map item_type to asset_type
          tickets: cartItem.tickets,
          price_per_ticket: pricePerTicket,
          status: 'payment_review',
          stage: 'stage_1_booking',
          stage_description: 'Payment submitted for review - Due diligence in progress'
        };

        console.log('Creating user asset:', assetData);

        // Add to user assets
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user-assets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(assetData)
        });

        if (!response.ok) {
          const errorData = await response.text();
          console.error('API Error:', response.status, errorData);
          throw new Error(`Failed to create user asset: ${response.status} ${errorData}`);
        }

        const result = await response.json();
        console.log('User asset created successfully:', result);

        // Remove from cart
        console.log('Removing cart item:', cartItem.id);
        await deleteCartItem(cartItem.id);
        console.log('Cart item removed successfully');
      }

      // Refresh data immediately
      console.log('Refreshing cart and assets data...');
      await loadCart();
      await fetchUserAssets();
      console.log('Data refreshed successfully');
      
      setShowPaymentModal(false);
      setActiveTab('assets'); // Switch to assets tab to show the moved items
      toast.success('Payment submitted successfully! Assets moved to My Assets section.');
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error(`Failed to process payment: ${error.message || 'Please try again.'}`);
    }
  };

  const fetchUserAssets = async () => {
    try {
      // Fetch user assets from the new endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user-assets`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user assets');
      }
      
      const userAssets = await response.json();
      
      // Fetch actual asset/package details for each user asset
      const assetsWithDetails = await Promise.all(
        userAssets.map(async (userAsset) => {
          try {
            let assetDetails;
            if (userAsset.asset_type === 'package' || userAsset.asset_type === 'curation_package') {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/curation-packages/${userAsset.asset_id}`);
              assetDetails = response.ok ? await response.json() : null;
            } else {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets/${userAsset.asset_id}`);
              assetDetails = response.ok ? await response.json() : null;
            }
            
            // Get the correct image based on asset type
            let assetImage = '';
            if (userAsset.asset_type === 'package' || userAsset.asset_type === 'curation_package') {
              assetImage = assetDetails?.package_image_url || assetDetails?.image_url || assetDetails?.featured_image || assetDetails?.images?.[0] || '';
            } else {
              assetImage = assetDetails?.exterior_images?.[0] || assetDetails?.images?.[0] || assetDetails?.hero_image || assetDetails?.featured_image || '';
            }
            
            return {
              ...userAsset,
              assetDetails,
              // Legacy fields for compatibility
              asset_id: userAsset.asset_id,
              asset_name: assetDetails?.name || 'Unknown Asset',
              asset_image: assetImage,
              tickets_purchased: userAsset.tickets,
              ownership_percentage: ((userAsset.tickets / 12) * 100).toFixed(2), // Assuming 12 total tickets
              amount_invested: userAsset.tickets * userAsset.price_per_ticket,
              total_paid: userAsset.total_paid || 0,
              status: userAsset.status,
              stage: userAsset.stage,
              stage_description: userAsset.stage_description
            };
          } catch (error) {
            console.error('Error fetching asset details:', error);
            return {
              ...userAsset,
              assetDetails: null,
              asset_name: 'Unknown Asset',
              asset_image: '',
              tickets_purchased: userAsset.tickets,
              ownership_percentage: ((userAsset.tickets / 12) * 100).toFixed(2),
              amount_invested: userAsset.tickets * userAsset.price_per_ticket,
              total_paid: userAsset.total_paid || 0,
              status: userAsset.status,
              stage: userAsset.stage,
              stage_description: userAsset.stage_description
            };
          }
        })
      );
      
      setMyAssets(assetsWithDetails);
    } catch (error) {
      console.error('Error fetching user assets:', error);
      toast.error('Failed to load your assets');
    }
  };

  const loadTickets = async () => {
    if (!user?.email) return;
    
    try {
      // Try to fetch from local backend first (as fallback)
      const localResponse = await axios.get(`${API_URL}/api/support/customer/${encodeURIComponent(user.email)}`, {
        withCredentials: true
      });
      setTickets(localResponse.data || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
      setTickets([]);
    }
  };

  // Helper function to get asset status display
  const getAssetStatus = (asset) => {
    // Use the new status and stage system
    if (asset.status && asset.stage) {
      switch (asset.status) {
        case 'expression_of_interest':
          return {
            status: 'Expression of Interest',
            subStatus: asset.stage_description || 'Interest submitted - Awaiting next steps',
            color: 'blue',
            action: null
          };
        case 'payment_review':
          return {
            status: 'Payment Under Review',
            subStatus: asset.stage_description || 'Payment screenshot submitted for verification',
            color: 'yellow',
            action: null
          };
        case 'stage_1_confirmed':
          return {
            status: 'Stage 1 Confirmed',
            subStatus: asset.stage_description || 'Due diligence in progress - Member group curation underway',
            color: 'blue',
            action: null
          };
        case 'stage_2_payment':
          return {
            status: 'Ready for Stage 2 Payment',
            subStatus: asset.stage_description || 'Member group confirmed - Proceed with full asset payment',
            color: 'purple',
            action: 'pay_stage2'
          };
        case 'stage_2_paid':
          return {
            status: 'Stage 2 Payment Complete',
            subStatus: asset.stage_description || 'Full payment completed - Asset deployment in progress',
            color: 'blue',
            action: null
          };
        case 'deployed':
          return {
            status: 'Asset Deployed',
            subStatus: asset.stage_description || 'Asset ready for use - Schedule your sessions now',
            color: 'green',
            action: 'schedule'
          };
        case 'active':
          return {
            status: 'Active Ownership',
            subStatus: asset.stage_description || 'Full fractional ownership active - Enjoy your asset!',
            color: 'green',
            action: 'schedule'
          };
        default:
          return {
            status: asset.status.replace('_', ' ').toUpperCase(),
            subStatus: asset.stage_description || '',
            color: 'gray',
            action: null
          };
      }
    }

    // Legacy support for assets without the new status system
    if (!asset.purchaseData) {
      return { 
        status: 'Booking Confirmed', 
        subStatus: 'Legacy booking - Please contact support for details',
        color: 'green',
        action: null
      };
    }

    const purchase = asset.purchaseData;
    
    // Stage 1 paid, waiting for tickets to be fulfilled
    if (purchase.stage_1_paid && !asset.allTicketsFulfilled) {
      return { 
        status: 'Awaiting Member Group', 
        subStatus: `${asset.totalTicketsSold}/12 tickets sold - Referring new members`,
        color: 'yellow',
        action: 'refer'
      };
    }

    // All tickets sold, waiting for stage 2 payment
    if (purchase.stage_1_paid && asset.allTicketsFulfilled && !purchase.stage_2_paid) {
      const deploymentDeadline = new Date(purchase.created_at);
      deploymentDeadline.setDate(deploymentDeadline.getDate() + 30);
      
      return { 
        status: 'Payment Required', 
        subStatus: 'All tickets sold - Time to complete your payment',
        color: 'purple',
        action: 'pay_stage2',
        purchaseId: purchase.id,
        countdown: true,
        deploymentDeadline
      };
    }

    // Stage 2 paid, waiting for deployment
    if (purchase.stage_2_paid && !asset.allStage2Paid) {
      return { 
        status: 'Awaiting Deployment', 
        subStatus: 'Your payment completed - Waiting for other members',
        color: 'blue',
        action: null
      };
    }

    // Fully deployed and ready
    if (purchase.stage_2_paid && asset.allStage2Paid) {
      return { 
        status: 'Deployed', 
        subStatus: 'Ready to use - Schedule your sessions',
        color: 'green',
        action: 'schedule'
      };
    }

    return { 
      status: 'Processing', 
      subStatus: 'Contact support for status update',
      color: 'gray',
      action: null
    };
  };

  // Handle referral
  const handleRefer = async (asset) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/referral/generate`,
        { asset_id: asset.asset_id },
        { withCredentials: true }
      );
      setReferralMessage(response.data.message);
      setSelectedAssetForReferral(asset);
      setShowReferralPopup(true);
    } catch (error) {
      toast.error('Failed to generate referral message');
    }
  };

  // Handle Stage 2 Payment
  const handlePayStage2 = (asset, purchaseId) => {
    navigate(`/transaction/${asset.asset_id}`, {
      state: {
        paymentStage: 'stage2',
        purchaseId: purchaseId,
        ticketsPurchased: asset.tickets_purchased
      }
    });
  };

  // Copy referral message
  const copyReferralMessage = () => {
    navigator.clipboard.writeText(referralMessage);
    toast.success('Referral message copied!');
  };


  // Handle Cancellation Request
  const handleCancellationRequest = (asset) => {
    setSelectedAssetForCancellation(asset);
    setShowCancellationModal(true);
  };

  const submitCancellationRequest = async (reason) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/user-assets/${selectedAssetForCancellation.id}/request-cancellation`,
        { cancellation_reason: reason },
        { 
          withCredentials: true
        }
      );

      toast.success('Cancellation request submitted successfully');
      
      // Refresh user assets to show updated state
      await fetchUserAssets();
      
      setShowCancellationModal(false);
      setSelectedAssetForCancellation(null);
    } catch (error) {
      console.error('Error submitting cancellation request:', error);
      toast.error('Failed to submit cancellation request');
      throw error;
    }
  };


  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/user/profile`,
        profileData,
        { withCredentials: true }
      );
      setUser(response.data.user);
      setEditMode(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const generateTicketNumber = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let ticket = 'THC';
    for (let i = 0; i < 7; i++) {
      ticket += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return ticket;
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    
    if (!ticketForm.subject || !ticketForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Submit through local backend (which proxies to admin portal)
      const response = await axios.post(
        `${API_URL}/api/support/tickets`,
        {
          customer_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Customer',
          customer_email: user?.email || '',
          customer_phone: user?.phone || '',
          subject: ticketForm.subject,
          message: ticketForm.message,
          priority: ticketForm.priority || 'medium'
        }
      );

      const ticket = response.data;
      setLastTicketNumber(ticket.ticket_number);
      setShowSuccessModal(true);
      setTicketForm({
        subject: '',
        message: '',
        priority: 'medium'
      });
      
      // Reload tickets to show the new one
      setTimeout(() => loadTickets(), 500);
    } catch (error) {
      console.error('Ticket submission error:', error);
      toast.error('Failed to submit ticket. Please try again.');
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    loadTickets();
  };

  const loadTicketConversation = async (ticketNumber) => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/support/tickets/${ticketNumber}/messages`
      );
      setTicketMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load conversation');
      setTicketMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    await loadTicketConversation(ticket.ticket_number);
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) {
      toast.error('Please enter a message');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/support/tickets/${selectedTicket.ticket_number}/customer-reply`,
        {
          customer_email: user?.email,
          message: replyMessage
        }
      );

      toast.success('Reply sent successfully');
      setReplyMessage('');
      // Reload the conversation
      await loadTicketConversation(selectedTicket.ticket_number);
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const closeTicketModal = () => {
    setSelectedTicket(null);
    setTicketMessages([]);
    setReplyMessage('');
    loadTickets(); // Refresh tickets list
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFFBF5' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFBF5' }}>
      <NavigationBar user={user} cartCount={cartCount} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 sm:mb-12">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" data-testid="dashboard-heading">My Dashboard</h1>
            <p className="text-gray-600 text-sm sm:text-base">Welcome back, {user?.name || 'Member'}</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="flex items-center gap-2 w-full sm:w-auto min-h-[44px]" data-testid="logout-btn">
            <LogOut size={18} />
            Logout
          </Button>
        </div>

        {/* Tabbed Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Sidebar - Menu (Horizontal on mobile, Vertical on desktop) */}
          <div className="w-full lg:w-64 flex-shrink-0">
            {/* Mobile: Horizontal scroll tabs */}
            <div className="lg:hidden overflow-x-auto pb-2 mb-4">
              <div className="flex gap-2 min-w-max">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg whitespace-nowrap transition-colors min-h-[44px] ${
                    activeTab === 'profile'
                      ? 'bg-gold text-black font-semibold'
                      : 'bg-white text-gray-700 border border-gray-200'
                  }`}
                >
                  <UserIcon size={18} />
                  <span className="text-sm">Profile</span>
                </button>
                <button
                  onClick={() => setActiveTab('cart')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg whitespace-nowrap transition-colors min-h-[44px] ${
                    activeTab === 'cart'
                      ? 'bg-gold text-black font-semibold'
                      : 'bg-white text-gray-700 border border-gray-200'
                  }`}
                >
                  <ShoppingCart size={18} />
                  <span className="text-sm">Cart</span>
                  {cartItems.length > 0 && (
                    <span className="bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cartItems.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('assets')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg whitespace-nowrap transition-colors min-h-[44px] ${
                    activeTab === 'assets'
                      ? 'bg-gold text-black font-semibold'
                      : 'bg-white text-gray-700 border border-gray-200'
                  }`}
                >
                  <Package size={18} />
                  <span className="text-sm">Assets</span>
                </button>
                {/* Support tab hidden - replaced by Concierge system
                <button
                  onClick={() => setActiveTab('support')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg whitespace-nowrap transition-colors min-h-[44px] ${
                    activeTab === 'support'
                      ? 'bg-gold text-black font-semibold'
                      : 'bg-white text-gray-700 border border-gray-200'
                  }`}
                >
                  <HelpCircle size={18} />
                  <span className="text-sm">Support</span>
                </button>
                */}
              </div>
            </div>

            {/* Desktop: Vertical sidebar */}
            <div className="hidden lg:block bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden sticky top-6">
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full flex items-center gap-3 px-6 py-4 text-left transition-colors ${
                  activeTab === 'profile'
                    ? 'bg-gold text-black font-semibold'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <UserIcon size={20} />
                <span>My Profile</span>
              </button>
              <button
                onClick={() => setActiveTab('cart')}
                className={`w-full flex items-center gap-3 px-6 py-4 text-left transition-colors border-t border-gray-200 ${
                  activeTab === 'cart'
                    ? 'bg-gold text-black font-semibold'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ShoppingCart size={20} />
                <span>My Cart</span>
                {cartItems.length > 0 && (
                  <span className="ml-auto bg-black text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                    {cartItems.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('assets')}
                className={`w-full flex items-center gap-3 px-6 py-4 text-left transition-colors border-t border-gray-200 ${
                  activeTab === 'assets'
                    ? 'bg-gold text-black font-semibold'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Package size={20} />
                <span>My Assets</span>
                {myAssets.length > 0 && (
                  <span className="ml-auto bg-black text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                    {myAssets.length}
                  </span>
                )}
              </button>
              {/* Support tab hidden - replaced by Concierge system
              <button
                onClick={() => setActiveTab('support')}
                className={`w-full flex items-center gap-3 px-6 py-4 text-left transition-colors border-t border-gray-200 ${
                  activeTab === 'support'
                    ? 'bg-gold text-black font-semibold'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <HelpCircle size={20} />
                <span>Support</span>
              </button>
              */}
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 min-w-0">
            {activeTab === 'profile' && (
              <div className="space-y-6 sm:space-y-8">
                {/* Profile Information */}
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 sm:p-6 md:p-8">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold">Profile Information</h2>
                    {!editMode ? (
                      <Button
                        onClick={() => setEditMode(true)}
                        className="flex items-center gap-2 bg-black text-white hover:bg-black/90 w-full sm:w-auto min-h-[44px]"
                      >
                        <Edit2 size={16} />
                        Edit Profile
                      </Button>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <Button
                          onClick={handleProfileUpdate}
                          className="flex items-center gap-2 bg-gold text-black hover:bg-gold/90 min-h-[44px]"
                          disabled={loading}
                        >
                          <Save size={16} />
                          Save Changes
                        </Button>
                        <Button
                          onClick={() => {
                            setEditMode(false);
                            setProfileData({
                              first_name: user.first_name || '',
                              last_name: user.last_name || '',
                              email: user.email || '',
                              phone: user.phone || '',
                              country_code: user.country_code || '+91',
                              whatsapp_number: user.whatsapp_number || '',
                              state: user.state || '',
                              city: user.city || ''
                            });
                          }}
                          variant="outline"
                          className="flex items-center gap-2 min-h-[44px]"
                        >
                          <X size={16} />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                      {editMode ? (
                        <Input
                          value={profileData.first_name}
                          onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                          className="w-full"
                        />
                      ) : (
                        <p className="text-gray-900 p-3 bg-gray-50 rounded-md">{user?.first_name || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                      {editMode ? (
                        <Input
                          value={profileData.last_name}
                          onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                          className="w-full"
                        />
                      ) : (
                        <p className="text-gray-900 p-3 bg-gray-50 rounded-md">{user?.last_name || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <p className="text-gray-900 p-3 bg-gray-50 rounded-md">{user?.email || 'N/A'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      {editMode ? (
                        <Input
                          value={profileData.phone}
                          onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                          className="w-full"
                        />
                      ) : (
                        <p className="text-gray-900 p-3 bg-gray-50 rounded-md">{user?.phone || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number</label>
                      {editMode ? (
                        <Input
                          value={profileData.whatsapp_number}
                          onChange={(e) => setProfileData({ ...profileData, whatsapp_number: e.target.value })}
                          className="w-full"
                        />
                      ) : (
                        <p className="text-gray-900 p-3 bg-gray-50 rounded-md">{user?.whatsapp_number || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      {editMode ? (
                        <Input
                          value={profileData.state}
                          onChange={(e) => setProfileData({ ...profileData, state: e.target.value })}
                          className="w-full"
                        />
                      ) : (
                        <p className="text-gray-900 p-3 bg-gray-50 rounded-md">{user?.state || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                      {editMode ? (
                        <Input
                          value={profileData.city}
                          onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                          className="w-full"
                        />
                      ) : (
                        <p className="text-gray-900 p-3 bg-gray-50 rounded-md">{user?.city || 'N/A'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Interest Form Status */}
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Expression of Interest Form</h2>
                  {user?.interest_form_submitted ? (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start sm:items-center mb-4">
                          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-lg sm:text-xl font-bold">Form Submitted</h3>
                            <p className="text-gray-600 text-xs sm:text-sm">Your preferences have been saved. View details to edit it.</p>
                          </div>
                        </div>
                      </div>
                      
                      <Button 
                        onClick={() => setShowInterestFormModal(true)}
                        className="bg-gold text-black hover:bg-gold/90 w-full sm:w-auto min-h-[44px]"
                      >
                        View Details
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-gray-600 mb-4 sm:mb-6 text-base sm:text-lg">You haven't submitted your interest preferences yet.</p>
                      <Link to="/register-interest">
                        <Button className="bg-black text-white hover:bg-black/90 px-6 sm:px-8 py-3 w-full sm:w-auto min-h-[44px]">Express Your Interest</Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'assets' && (
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">My Assets</h2>
                {myAssets.length === 0 ? (
                  <div className="bg-white p-6 sm:p-8 md:p-12 rounded-lg text-center shadow-md border border-gray-200">
                    <p className="text-gray-600 mb-4 sm:mb-6 text-base sm:text-lg">You haven't invested in any assets yet.</p>
                    <Link to="/high-assets">
                      <Button className="bg-black text-white hover:bg-black/90 px-6 sm:px-8 py-3 w-full sm:w-auto min-h-[44px]">Browse Assets</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-6 sm:space-y-8">
                    {myAssets.map((asset) => {
                      const statusInfo = getAssetStatus(asset);
                      const isCancellationPending = asset.cancellation_requested && asset.cancellation_status === 'pending';
                      
                      return (
                        <div 
                          key={asset.id} 
                          className={`rounded-lg shadow-lg border overflow-hidden ${
                            isCancellationPending ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200'
                          }`}
                          style={isCancellationPending ? { opacity: 0.8 } : {}}
                        >
                          <div className="p-4 sm:p-6">
                            {/* Top Section: Large Asset Image + All Content to Right */}
                            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                              {/* Large Asset Image - Horizontal Landscape */}
                              <div className="w-full sm:w-56 h-48 sm:h-40 flex-shrink-0">
                                <img 
                                  src={asset.asset_image} 
                                  alt={asset.asset_name} 
                                  className="w-full h-full object-cover rounded-lg"
                                  style={isCancellationPending ? { filter: 'grayscale(100%)' } : {}}
                                />
                              </div>
                              
                              {/* All Content on Right Side */}
                              <div className="flex-1 flex flex-col justify-between">
                                {/* Asset Name and ID */}
                                <div className="mb-4">
                                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                                    <h3 className="text-lg sm:text-xl font-bold break-words">{asset.asset_name}</h3>
                                    {asset.assetDetails?.asset_identification_number && (
                                      <div className="text-left sm:text-right flex-shrink-0">
                                        <p className="text-xs text-gray-600">Asset ID</p>
                                        <p className="text-sm font-semibold text-gold">{asset.assetDetails.asset_identification_number}</p>
                                      </div>
                                    )}
                                  </div>
                                
                                  {/* Status Display */}
                                  <div className={`p-3 rounded-lg text-sm ${
                                    isCancellationPending ? 'bg-red-50 border border-red-300' :
                                    statusInfo.color === 'yellow' ? 'bg-yellow-50 border border-yellow-300' :
                                    statusInfo.color === 'blue' ? 'bg-blue-50 border border-blue-300' :
                                    statusInfo.color === 'purple' ? 'bg-purple-50 border border-purple-300' :
                                    statusInfo.color === 'green' ? 'bg-green-50 border border-green-300' :
                                    'bg-gray-50 border border-gray-300'
                                  }`}>
                                    <p className="text-xs font-semibold text-gray-700">
                                      Status: <span className={`${
                                        isCancellationPending ? 'text-red-700' :
                                        statusInfo.color === 'yellow' ? 'text-yellow-700' :
                                        statusInfo.color === 'blue' ? 'text-blue-700' :
                                        statusInfo.color === 'purple' ? 'text-purple-700' :
                                        statusInfo.color === 'green' ? 'text-green-700' :
                                        'text-gray-700'
                                      }`}>
                                        {isCancellationPending ? 'Cancellation Request Under Review' : statusInfo.status}
                                        {statusInfo.countdown && statusInfo.deploymentDeadline && !isCancellationPending && (
                                          <span className="ml-2 inline-block">
                                            <CountdownTimer targetDate={statusInfo.deploymentDeadline} />
                                          </span>
                                        )}
                                      </span>
                                    </p>
                                    {statusInfo.ticketsInfo && !isCancellationPending && (
                                      <p className="text-xs text-gray-600 mt-1">{statusInfo.ticketsInfo}</p>
                                    )}
                                    {!isCancellationPending && statusInfo.subStatus && (
                                      <p className="text-xs text-gray-600 mt-1">{statusInfo.subStatus}</p>
                                    )}
                                    {isCancellationPending && (
                                      <p className="text-xs text-red-600 mt-1">Your cancellation request is being reviewed by our team.</p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Investment Details */}
                                <div className={`grid gap-3 sm:gap-3 ${asset.asset_type === 'package' || asset.asset_type === 'curation_package' ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
                                  {/* Only show Tickets and Price per ticket for individual assets, not packages */}
                                  {asset.asset_type !== 'package' && asset.asset_type !== 'curation_package' && (
                                    <>
                                      <div>
                                        <p className="text-xs text-gray-600 whitespace-nowrap">Tickets</p>
                                        <p className="text-base sm:text-lg font-bold text-gold">{asset.tickets_purchased}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-gray-600 whitespace-nowrap">Price per ticket</p>
                                        <p className="text-base sm:text-lg font-semibold">₹{((asset.amount_invested || 0) / (asset.tickets_purchased || 1) / 100000).toFixed(1)}L</p>
                                      </div>
                                    </>
                                  )}
                                  <div>
                                    <p className="text-xs text-gray-600 whitespace-nowrap">Total Value</p>
                                    <p className="text-base sm:text-lg font-semibold">₹{((asset.amount_invested || 0) / 100000).toFixed(1)}L</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-600 whitespace-nowrap">Total Paid</p>
                                    <p className="text-base sm:text-lg font-semibold text-green-600">₹{((asset.total_paid || 0) / 100000).toFixed(1)}L</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                              
                            {/* Process Tracker - Full Width Single Line */}
                            <div className="mb-4 sm:mb-6">
                              <ProcessTracker 
                                currentStatus={asset.status} 
                                stage={asset.stage}
                                layout="single-line"
                              />
                            </div>
                              
                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
                              {!isCancellationPending && statusInfo.action === 'refer' && (
                                <Button 
                                  onClick={() => handleRefer(asset)}
                                  className="bg-gold text-black hover:bg-gold/90 w-full sm:w-auto min-h-[44px]"
                                >
                                  Refer Now
                                </Button>
                              )}
                              
                              {!isCancellationPending && statusInfo.action === 'pay_stage2' && (
                                <Button 
                                  onClick={() => handlePayStage2(asset, statusInfo.purchaseId)}
                                  className="bg-blue-600 text-white hover:bg-blue-700 w-full sm:w-auto min-h-[44px]"
                                >
                                  Pay Now
                                </Button>
                              )}
                                
                              {/* Pay Button - Gold with Border and Shadow */}
                              {!isCancellationPending && (
                                <Button 
                                  onClick={() => {
                                    // Set current asset as cart item and open payment modal
                                    setCartItems([{
                                      id: asset.id,
                                      asset_id: asset.asset_id,
                                      item_type: asset.asset_type || 'asset',
                                      tickets: asset.tickets_purchased,
                                      price_per_ticket: (asset.amount_invested || 0) / (asset.tickets_purchased || 1)
                                    }]);
                                    setCartAssets({
                                      [asset.asset_id]: asset.assetDetails
                                    });
                                    setShowPaymentModal(true);
                                  }}
                                  style={{ backgroundColor: '#D4AF37', color: '#000' }}
                                  className="border border-gray-300 shadow-sm hover:opacity-90 w-full sm:w-auto min-h-[44px] transition-all duration-200 hover:scale-105"
                                >
                                  Pay
                                </Button>
                              )}
                              
                              {/* Request Cancellation Button */}
                              {isCancellationPending ? (
                                <Button 
                                  disabled
                                  style={{ backgroundColor: '#DC2626', color: '#000', cursor: 'not-allowed' }}
                                  className="w-full sm:w-auto min-h-[44px] opacity-90"
                                >
                                  Cancellation Requested
                                </Button>
                              ) : (
                                <Button 
                                  onClick={() => handleCancellationRequest(asset)}
                                  className="bg-white text-black border border-gray-300 hover:bg-gray-50 w-full sm:w-auto min-h-[44px]"
                                >
                                  Request Cancellation
                                </Button>
                              )}
                              
                              {/* Schedule Usage Button - Collapsible with booking status */}
                              {(asset.status === 'deployed' || asset.status === 'active') ? (
                                <Button 
                                  className="bg-green-600 text-white hover:bg-green-700 w-full sm:w-auto min-h-[44px] transition-transform duration-200 hover:scale-105"
                                  onClick={() => {
                                    setExpandedSchedulers(prev => ({
                                      ...prev,
                                      [asset.id]: !prev[asset.id]
                                    }));
                                  }}
                                >
                                  {expandedSchedulers[asset.id] ? 'Hide' : 'Schedule Usage'}
                                </Button>
                              ) : (
                                <Button 
                                  disabled
                                  className="bg-gray-300 text-gray-500 cursor-not-allowed w-full sm:w-auto min-h-[44px]"
                                  title="Available when asset is deployed"
                                >
                                  Schedule Usage
                                </Button>
                              )}
                            </div>

                            {/* Next Booking Status Display */}
                            {asset.next_booking_status && asset.next_booking_status !== 'none' && (
                              <div className={`mb-4 p-3 rounded-lg border ${
                                asset.next_booking_status === 'confirmed' ? 'bg-green-50 border-green-300' :
                                asset.next_booking_status === 'rejected' ? 'bg-red-50 border-red-300' :
                                'bg-yellow-50 border-yellow-300'
                              }`}>
                                <p className="text-sm font-semibold text-gray-700 mb-1">
                                  Next Booking: {new Date(asset.next_booking_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} to {new Date(asset.next_booking_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                                <p className={`text-sm font-medium ${
                                  asset.next_booking_status === 'confirmed' ? 'text-green-700' :
                                  asset.next_booking_status === 'rejected' ? 'text-red-700' :
                                  'text-yellow-700'
                                }`}>
                                  Status: {
                                    asset.next_booking_status === 'pending' ? 'Request under review' :
                                    asset.next_booking_status === 'confirmed' ? 'Dates confirmed' :
                                    asset.next_booking_status === 'rejected' ? 'Dates unavailable, choose alternative dates' :
                                    asset.next_booking_status
                                  }
                                </p>
                              </div>
                            )}

                            {/* Real-time Scheduler - Collapsible */}
                            <AssetScheduler
                              asset={asset}
                              onBookingComplete={fetchUserAssets}
                              isExpanded={expandedSchedulers[asset.id]}
                              onToggle={() => {
                                setExpandedSchedulers(prev => ({
                                  ...prev,
                                  [asset.id]: !prev[asset.id]
                                }));
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'cart' && (
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-6">My Cart</h2>
                
                {cartItems.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
                    <ShoppingCart size={64} className="mx-auto mb-4 text-gray-300" />
                    <h3 className="text-xl font-semibold mb-2">Your cart is empty</h3>
                    <p className="text-gray-600 mb-6">Start adding assets to your cart by clicking "Express Interest" on asset details pages.</p>
                    <Button
                      onClick={() => navigate('/high-assets')}
                      className="bg-black text-white hover:bg-black/90"
                    >
                      Browse Assets
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4 mb-6">
                      {cartItems.map((item) => {
                        const asset = cartAssets[item.asset_id];
                        if (!asset) return null;

                        // Get image - handle both asset and package formats
                        const getItemImage = () => {
                          if (item.item_type === 'curation_package') {
                            return asset.package_image_url || asset.image_url || asset.featured_image || asset.images?.[0] || 'https://via.placeholder.com/400x300?text=Package+Image';
                          }
                          return asset.exterior_images?.[0] || asset.featured_image || asset.images?.[0] || 'https://via.placeholder.com/400x300?text=Asset+Image';
                        };

                        // Get price - handle both asset and package formats
                        const getItemPrice = () => {
                          if (item.item_type === 'curation_package') {
                            return asset.complete_package_price || asset.price || 0;
                          }
                          return asset.price_per_ticket || 0;
                        };

                        return (
                          <div key={item.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 md:p-6">
                            <div className="flex flex-col md:flex-row gap-4">
                              {/* Item Image */}
                              <div className="w-full md:w-32 h-32 flex-shrink-0">
                                <img 
                                  src={getItemImage()} 
                                  alt={asset.name || asset.title}
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              </div>

                              {/* Item Details */}
                              <div className="flex-1">
                                <div className="flex items-start gap-2 mb-2">
                                  <h3 className="text-lg md:text-xl font-bold">{asset.name || asset.title}</h3>
                                  {item.item_type === 'curation_package' && (
                                    <span className="text-xs bg-gold/20 text-gold px-2 py-1 rounded-full">Package</span>
                                  )}
                                </div>
                                
                                {/* Show vehicle names for curation packages, description for assets */}
                                {item.item_type === 'curation_package' && asset.vehicles && asset.vehicles.length > 0 ? (
                                  <p className="text-sm text-gray-600 mb-2">
                                    {asset.vehicles.map((vehicle, idx) => (
                                      <span key={idx}>
                                        <span className="font-medium">{vehicle.segment || vehicle.category}:</span> {vehicle.name}
                                        {idx < asset.vehicles.length - 1 ? ', ' : ''}
                                      </span>
                                    ))}
                                  </p>
                                ) : (
                                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{asset.description}</p>
                                )}
                                
                                <div className="flex flex-wrap gap-4 items-center text-sm">
                                  {item.item_type === 'curation_package' ? (
                                    <span className="font-semibold text-gold">₹{getItemPrice().toLocaleString('en-IN')}</span>
                                  ) : (
                                    <>
                                      <span className="font-semibold text-gold">₹{getItemPrice().toLocaleString('en-IN')}/ticket</span>
                                      <span className="text-gray-600">{item.tickets} ticket(s) = {item.tickets * 30} days/year</span>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex flex-col gap-2 md:items-end">
                                {editingCartItem === item.id && item.item_type !== 'curation_package' ? (
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        const newTickets = Math.max(1, item.tickets - 1);
                                        handleUpdateCartItem(item.id, newTickets);
                                      }}
                                    >
                                      <Minus size={16} />
                                    </Button>
                                    <span className="w-12 text-center font-semibold">{item.tickets}</span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        const availableTickets = asset.total_tickets - asset.sold_tickets;
                                        const newTickets = Math.min(availableTickets, item.tickets + 1);
                                        handleUpdateCartItem(item.id, newTickets);
                                      }}
                                    >
                                      <Plus size={16} />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditingCartItem(null)}
                                    >
                                      <Check size={16} />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex gap-2">
                                    {item.item_type !== 'curation_package' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditingCartItem(item.id)}
                                        className="flex items-center gap-1"
                                      >
                                        <Edit2 size={14} />
                                        Edit
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleDeleteCartItem(item.id)}
                                      className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 size={14} />
                                      Remove
                                    </Button>
                                  </div>
                                )}
                                
                                <div className="text-right mt-2">
                                  <span className="text-lg font-bold">
                                    ₹{item.item_type === 'curation_package' 
                                      ? (asset.complete_package_price || asset.price || 0).toLocaleString('en-IN')
                                      : ((asset.price_per_ticket || 0) * item.tickets).toLocaleString('en-IN')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Cart Summary */}
                    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                      <h3 className="text-xl font-bold mb-4">Cart Summary</h3>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span>Total Assets:</span>
                          <span className="font-semibold">{cartItems.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Tickets:</span>
                          <span className="font-semibold">{cartItems.filter(i => i.item_type !== 'curation_package').reduce((sum, item) => sum + item.tickets, 0)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                          <span>Total Amount:</span>
                          <span className="text-gold">
                            ₹{cartItems.reduce((sum, item) => {
                              const itemData = cartAssets[item.asset_id];
                              if (!itemData) return sum;
                              
                              if (item.item_type === 'curation_package') {
                                return sum + (itemData.complete_package_price || itemData.price || 0);
                              } else {
                                return sum + (itemData.price_per_ticket || 0) * item.tickets;
                              }
                            }, 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row gap-3">
                        <Button 
                          variant="outline" 
                          onClick={() => document.getElementById('get-in-touch')?.scrollIntoView({ behavior: 'smooth' })}
                          className="flex-1 border-black text-black hover:bg-black hover:text-white"
                        >
                          Contact Support
                        </Button>
                        <Button
                          onClick={handleProceedToExpressInterest}
                          className="flex-1 bg-black text-white hover:bg-black/90"
                        >
                          Proceed to Express Interest
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'support' && (
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Support</h2>
                
                {/* Submit Ticket Form */}
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
                  <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Submit a Support Ticket</h3>
                  
                  <form onSubmit={handleSupportSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                        <Input
                          value={ticketForm.first_name}
                          onChange={(e) => setTicketForm({ ...ticketForm, first_name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                        <Input
                          value={ticketForm.last_name}
                          onChange={(e) => setTicketForm({ ...ticketForm, last_name: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <Input
                          type="email"
                          value={ticketForm.email}
                          onChange={(e) => setTicketForm({ ...ticketForm, email: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                        <Input
                          value={ticketForm.contact_number}
                          onChange={(e) => setTicketForm({ ...ticketForm, contact_number: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                      <Input
                        value={ticketForm.subject}
                        onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                      <textarea
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold"
                        rows="5"
                        value={ticketForm.message}
                        onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
                        required
                      />
                    </div>

                    <Button type="submit" className="w-full bg-gold hover:bg-gold/90 text-black font-semibold min-h-[44px]">
                      Submit Ticket
                    </Button>
                  </form>
                </div>

                {/* My Tickets */}
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 sm:p-6 md:p-8">
                  <h3 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">My Tickets</h3>
                  
                  {tickets.length === 0 ? (
                    <p className="text-gray-600 text-center py-6 sm:py-8 text-sm sm:text-base">No support tickets yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {tickets.map((ticket) => (
                        <div key={ticket.id} className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                          <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-3">
                            <div className="flex-1">
                              <p className="font-semibold text-base sm:text-lg">Ticket #{ticket.ticket_number}</p>
                              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                {new Date(ticket.created_at).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap ${
                                ticket.status === 'open' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : ticket.status === 'in_progress'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {ticket.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <p className="font-medium mb-2 text-sm sm:text-base">{ticket.subject}</p>
                          <p className="text-xs sm:text-sm text-gray-600 mb-3 line-clamp-2">{ticket.message}</p>
                          <Button
                            onClick={() => handleViewTicket(ticket)}
                            className="bg-gold text-black hover:bg-gold/90 text-sm w-full sm:w-auto"
                          >
                            View Conversation
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center shadow-2xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-3">Ticket Submitted Successfully!</h3>
            <p className="text-gray-600 mb-2">Your support ticket has been created.</p>
            <p className="text-lg font-semibold text-gold mb-6">Ticket Number: {lastTicketNumber}</p>
            <p className="text-sm text-gray-500 mb-6">We'll get back to you within 24-48 hours.</p>
            <Button 
              onClick={closeSuccessModal}
              className="bg-gold hover:bg-gold/90 text-black font-semibold px-8"
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Thank You Modal for Interest Submission */}
      {showThankYouModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center shadow-2xl">
            <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-4">Thank You for Your Interest!</h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              We truly appreciate your interest in our exclusive collection. Our dedicated concierge team will personally reach out to you shortly to discuss the next steps and answer any questions you may have.
            </p>
            <Button 
              onClick={() => setShowThankYouModal(false)}
              className="bg-gold hover:bg-gold/90 text-black font-semibold px-8 py-3"
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Referral Popup */}
      {showReferralPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">Refer a Friend</h3>
              <button 
                onClick={() => setShowReferralPopup(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Share this referral message with your friends to join you in owning <strong>{selectedAssetForReferral?.asset_name}</strong>!
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{referralMessage}</p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={copyReferralMessage}
                className="flex-1 bg-gold text-black hover:bg-gold/90"
              >
                Copy Message
              </Button>
              <Button 
                onClick={() => setShowReferralPopup(false)}
                variant="outline"
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Interest Form Modal */}
      {showInterestFormModal && (
        <InterestFormModal
          user={user}
          setUser={setUser}
          onClose={() => setShowInterestFormModal(false)}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          cartItems={cartItems}
          cartAssets={cartAssets}
          totalAmount={cartItems.reduce((sum, item) => sum + (item.tickets * (item.price_per_ticket || 0)), 0)}
          onPaymentSubmit={handlePaymentSubmit}
        />
      )}

      {/* Cancellation Modal */}
      {showCancellationModal && selectedAssetForCancellation && (
        <CancellationModal
          isOpen={showCancellationModal}
          onClose={() => {
            setShowCancellationModal(false);
            setSelectedAssetForCancellation(null);
          }}
          onSubmit={submitCancellationRequest}
          assetName={selectedAssetForCancellation.asset_name}
        />
      )}

      {/* Ticket Conversation Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h2 className="text-2xl font-bold">Ticket #{selectedTicket.ticket_number}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Created: {new Date(selectedTicket.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <button
                  onClick={closeTicketModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <span className={`px-4 py-1 rounded-full text-sm font-semibold ${
                  selectedTicket.status === 'open' 
                    ? 'bg-blue-100 text-blue-800' 
                    : selectedTicket.status === 'in_progress'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {(selectedTicket.status || 'open').replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="mt-3 p-3 bg-gray-50 rounded">
                <p className="font-semibold mb-1">{selectedTicket.subject || 'No subject'}</p>
                <p className="text-sm text-gray-700">{selectedTicket.message || 'No message content'}</p>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loadingMessages ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gold"></div>
                </div>
              ) : ticketMessages.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No replies yet. Our team will respond soon.</p>
              ) : (
                ticketMessages.map((message, index) => {
                  if (!message || !message.message) return null;
                  return (
                    <div key={index} className={`flex ${message.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg p-4 ${
                        message.sender_type === 'customer'
                          ? 'bg-gold/20 text-black'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">
                            {message.sender_type === 'customer' ? 'You' : 'Support Team'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {message.created_at ? new Date(message.created_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Just now'}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Reply Input */}
            <div className="p-6 border-t border-gray-200">
              <div className="flex gap-2">
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply here..."
                  className="flex-1 border border-gray-300 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gold"
                  rows="3"
                />
                <Button
                  onClick={handleSendReply}
                  disabled={!replyMessage.trim()}
                  className="bg-gold text-black hover:bg-gold/90 self-end"
                >
                  Send Reply
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Your reply will be sent to our support team
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Get In Touch Footer */}
      <div id="get-in-touch">
        <Footer />
      </div>
    </div>
  );
};

export default MemberDashboard; 
